import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "@/api/client";
import { useStore } from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";

export function LoginPage() {
  const { dispatch } = useStore();
  // Der Login ist das Erste, was die Gäste sehen — die Beschriftung gehört
  // zum Offsite, nicht fest ins Frontend.
  const config = useOffsiteConfig();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [autoToast, setAutoToast] = useState(false);
  const autoTried = useRef(false);

  // If the api-client kicked us back here on 401, surface a friendly message.
  useEffect(() => {
    if (params.get("session") === "expired") {
      setError("Deine Session ist abgelaufen — bitte erneut anmelden.");
    }
  }, [params]);

  // Convenience: if someone shares a Magic-Link as `/?token=...` instead of
  // `/auth/magic?token=...`, hop over to the magic-link route so the
  // standard redemption flow takes over.
  useEffect(() => {
    const t = params.get("token");
    if (t) {
      navigate(`/auth/magic?token=${encodeURIComponent(t)}`, { replace: true });
    }
  }, [params, navigate]);

  const tryLogin = async (pw: string) => {
    setError(null);
    setLoading(true);
    try {
      const { token, role } = await api.auth.login(pw);
      dispatch({ type: "auth/login", token, role });
      const isWideViewport = window.innerWidth >= 1024;
      if (role === "presenter" && isWideViewport) navigate("/present", { replace: true });
      else navigate("/participate", { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError && err.status === 401 ? "Passwort falsch." : "Login fehlgeschlagen.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Auto-login via ?p=PASSWORD URL param (used by QR-Code on slide 8).
  useEffect(() => {
    if (autoTried.current) return;
    const auto = params.get("p");
    if (!auto) return;
    autoTried.current = true;
    setPassword(auto);
    setAutoToast(true);
    // Strip the password from the URL so it doesn't sit in browser history.
    try {
      window.history.replaceState(null, "", "/");
    } catch {
      /* noop */
    }
    tryLogin(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Auto-toast disappears after 3s.
  useEffect(() => {
    if (!autoToast) return;
    const id = setTimeout(() => setAutoToast(false), 3000);
    return () => clearTimeout(id);
  }, [autoToast]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    tryLogin(password);
  };

  return (
    <main className="login-hero">
      <div className="login-hero-bg" aria-hidden="true" />
      {autoToast && (
        <div className="login-autotoast" role="status" aria-live="polite">
          <span aria-hidden="true">⚡</span>
          Über QR-Code eingeloggt
        </div>
      )}
      <form onSubmit={onSubmit} className="login-card">
        <header style={{ display: "grid", gap: 4 }}>
          <div className="deck-eyebrow">{config.displayName}</div>
          <h1
            className="deck-title"
            style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", margin: 0 }}
          >
            Willkommen
          </h1>
          <p className="form-hint" style={{ marginTop: 6 }}>
            Bitte gib das Workshop-Passwort ein.
          </p>
        </header>

        {error && <p className="form-error">{error}</p>}

        <label className="login-field">
          <span className="login-field-label">Workshop-Passwort</span>
          <input
            className="input-text"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="one-time-code"
            required
            autoFocus
          />
        </label>

        <button
          type="submit"
          disabled={loading || !password}
          className="btn btn-primary btn-block btn-lg"
        >
          {loading ? "…" : "Anmelden"}
        </button>
      </form>
    </main>
  );
}
